import useAccount from '@app/features/Account/store/useAccount';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import {
  canDownload,
  downloadBlob,
  useResourceActions,
} from '@app/shared/helpers/file';
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';
import { Resource } from '@app/types/blackroom-api';
import {
  ResourceStorageVariantEnum,
  ResourceStorageVariantPermissionMap,
} from '@app/types/blackroom-app';
import { getViewAsRequestHeaders } from '@app/shared/providers/ViewAsProvider/store';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Group, Menu, Stack, Tooltip } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import {
  IconDownload,
  IconEdit,
  IconMessageQuestion,
  IconShare,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { extractFilenameFromHeaders } from '../../utils/extractFilenameFromHeaders';
import { useState } from 'react';
import { useCopyShareLink } from '../../hooks/useCopyShareLink';
import { useReactiveVar } from '@apollo/client';
import dialogStoreVar from '@app/features/Dialog/store/dialogStore';
import LabelStorageVariant from '@ui-kit/atoms/LabelStorageVariant';

function ActionButtons(props: { resource: Resource; closeMenu?: () => void }) {
  const workspace = useWorkspace();
  const { isWorkspaceAdmin } = useAccount();
  const actions = useResourceActions();
  const { getAccessTokenSilently } = useAuth0();
  const { copyShareLink } = useCopyShareLink();
  const dialogStore = useReactiveVar(dialogStoreVar);

  const { t } = useTranslation();

  const isAdmin = isWorkspaceAdmin(workspace);

  const [loadingDownloadUrlForPermission, setLoadingDownloadUrlForPermission] =
    useState<boolean>(false);

  function onDeleteResource() {
    actions.delete.action({
      resources: [props.resource],
      resetRowSelection: props.closeMenu,
    });
  }

  const openRedactionDialog = () => {
    dialogStore?.invokeDialog?.('dialog-redaction', {
      modalProps: {
        title: t('Redaction:DialogRedaction.title'),
      },
      resourcesToRedact: [props.resource],
    });
  };

  const downloadResource = async (
    storageVariant: ResourceStorageVariantEnum,
  ) => {
    setLoadingDownloadUrlForPermission(true);
    try {
      const permission = ResourceStorageVariantPermissionMap[storageVariant];
      const token = await getAccessTokenSilently();
      const res = await fetch(
        `${import.meta.env.VITE_API_CORE_REST_URL}/resources/download/${workspace.id}/${props.resource.id}?permission=${permission}`,
        {
          headers: {
            authorization: token ? `Bearer ${token}` : '',
            ...getViewAsRequestHeaders(),
          },
        },
      );

      if (res.status === 202) {
        showNotification({
          title: t('Resource:Common.operation.download.zipEmailDownload.title'),
          message: t(
            'Resource:Common.operation.download.zipEmailDownload.message',
          ),
          color: 'blackroomGreen',
        });
        return;
      }

      if (res.status !== 200) {
        showNotification({
          title: t('Common:errors.genericTitle'),
          message: t('Common:errors.genericMessage'),
          color: 'blackroomRed',
        });
        return;
      }

      const blob = await res.blob();
      if (blob) {
        downloadBlob(
          blob,
          extractFilenameFromHeaders(res.headers, props.resource.name),
        );
      } else {
        showNotification({
          title: t('Common:errors.genericTitle'),
          message: t('Common:errors.genericMessage'),
          color: 'blackroomRed',
        });
      }
    } catch {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
    } finally {
      setLoadingDownloadUrlForPermission(false);
    }
  };

  function renderDownloadAction() {
    if (!canDownload(props.resource)) {
      return null;
    }

    if (isAdmin) {
      return (
        <Menu position="bottom" radius="sm" shadow="sm" withinPortal>
          <Menu.Target>
            <Box>
              <Tooltip label={t('Common:cta.download')}>
                <Button
                  aria-label={t('Common:cta.download')}
                  variant="light"
                  size="md"
                  radius="xs"
                  px="xs"
                  loading={loadingDownloadUrlForPermission}
                  disabled={!props.resource.canDownload}
                >
                  <IconDownload size={ICON_SIZE_MAP.small} />
                </Button>
              </Tooltip>
            </Box>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              disabled={loadingDownloadUrlForPermission}
              onClick={() =>
                downloadResource(ResourceStorageVariantEnum.original)
              }
            >
              <LabelStorageVariant
                storageVariant={ResourceStorageVariantEnum.original}
              />
            </Menu.Item>
            <Menu.Item
              disabled={loadingDownloadUrlForPermission}
              onClick={() =>
                downloadResource(ResourceStorageVariantEnum.redacted)
              }
            >
              <LabelStorageVariant
                storageVariant={ResourceStorageVariantEnum.redacted}
              />
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <Tooltip label={t('Common:cta.download')}>
        <Button
          variant="light"
          size="md"
          radius="xs"
          px="xs"
          loading={loadingDownloadUrlForPermission}
          disabled={!props.resource.canDownload}
          onClick={() => downloadResource(ResourceStorageVariantEnum.redacted)}
        >
          <IconDownload size={ICON_SIZE_MAP.small} />
        </Button>
      </Tooltip>
    );
  }

  function getResourceActions() {
    return (
      <Group gap="xs" justify="center">
        {renderDownloadAction()}

        {isAdmin && (
          <>
            <Tooltip label={t('Common:cta.delete')}>
              <Button
                variant="light"
                size="md"
                radius="xs"
                px="xs"
                onClick={() => onDeleteResource()}
              >
                <IconTrash size={ICON_SIZE_MAP.small} />
              </Button>
            </Tooltip>

            {props.resource.pspdfKitId && (
              <Tooltip label={t('Common:cta.redact')}>
                <Button
                  variant="light"
                  size="md"
                  radius="xs"
                  px="xs"
                  onClick={() => openRedactionDialog()}
                >
                  <IconEdit size={ICON_SIZE_MAP.small} />
                </Button>
              </Tooltip>
            )}
          </>
        )}

        <Tooltip label={t('Common:cta.ask')}>
          <Button
            variant="light"
            size="md"
            radius="xs"
            px="xs"
            onClick={() => {
              dialogStore?.invokeDialog?.('dialog-support-question', {
                modalProps: {
                  title: t('Support:DialogQuestionCreate.title'),
                  withCloseButton: false,
                },
                resourceId: props.resource.id,
              });
            }}
          >
            <IconMessageQuestion size={ICON_SIZE_MAP.small} />
          </Button>
        </Tooltip>

        <Tooltip label={t('Resource:Common.actions.share')}>
          <Button
            variant="light"
            size="md"
            radius="xs"
            px="xs"
            onClick={() => copyShareLink(props.resource)}
          >
            <IconShare size={ICON_SIZE_MAP.small} />
          </Button>
        </Tooltip>
      </Group>
    );
  }

  return <Stack>{getResourceActions()}</Stack>;
}

export default ActionButtons;
